import { useAppLogoAsset } from "@shared/hooks/useAppLogoAsset";
import { BrandSwitchPrompt } from "@shared/components/BrandSwitchPrompt";
import { useLoginDomainConfig } from "./login-domain-config";
import { useAuth } from "@shared/hooks/useAuth";
import { UserAvatar } from "@features/Chat/components/UserAvatar";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Skeleton,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { BulletSeparator } from "@shared/components/BulletSeparator";
import { Form } from "@shared/components/Form";
import { TextField } from "@shared/components/TextField";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AuthContainer,
  LoadingContainer,
  SelectedLoginOption,
  MembershipAffiliationContainer,
} from "./Auth.styles";
import { LoginFormSchema } from "./auth-form-helpers";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { OAuthAuthorization } from "./OAuthAutorization";
import { OAuthClientInfoBox } from "./components/OAuthClientInfoBox";
import {
  getLoginOptionById,
  getLoginPathByOption,
  isMlsSsoLoginOption,
  LOGIN_OPTION_STORAGE_KEY,
  LoginOption,
  useLoginOptions,
} from "./login-options-form-helpers";
import { LoginSSO } from "./LoginSSO";
import { GoogleSsoButton } from "@shared/components/GoogleSsoButton";
import { appConfig } from "@shared/utils/appConfig";
import { INDIVIDUAL_PLAN_ID } from "@shared/utils/constants";
import { INDIVIDUAL_PLAN } from "@features/Subscription/individual-plan";
import { useHandleSSOErrors } from "@shared/hooks/useHandleSSOErrors";
import { useLoginLogic } from "./hooks/useLoginLogic";
import { toast } from "@shared/services/toast";
import { PasswordTextField } from "@shared/components/PasswordTextField";
import { createAutocompleteFilterOptionsFunction } from "@shared/utils/autocomplete-extentions";
import { Divider, SubmitButton } from "@avenue-8/sidekick-ui";
import { ResponsiveSheetDialog } from "@shared/components/ResponsiveSheetDialog/ResponsiveSheetDialog";
const LoginSSOMock = lazy(() =>
  import("@features/Auth/LoginSSO.mock").then((module) => ({
    default: module.LoginSSOMock,
  })),
);

type Props = {
  oauthData: {
    clientId: string;
    scope: string;
    redirectUri: string;
    state: string | null;
    responseType: string;
  } | null;
  redirect: string | null;
  welcome: string | null;
};

const isDev = appConfig.APP_ENV === "development";

const filterOptions = createAutocompleteFilterOptionsFunction<LoginOption>([
  "name",
  "abbreviation",
]);

export const Login = (props: Props) => {
  const { oauthData, redirect, welcome } = props;
  const { src: logoSrc, alt: logoAlt } = useAppLogoAsset();
  const loginDomainConfig = useLoginDomainConfig();
  const supportsMembershipAffiliation =
    loginDomainConfig.membershipAffiliationLabel !== null;
  const [isEditMlsOptionEnabled, setIsEditMlsOptionEnabled] = useState(false);
  const membershipAffiliationRef = useRef<HTMLInputElement>(null);
  const [membershipAffiliationOpen, setMembershipAffiliationOpen] =
    useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { options: availableLoginOptions, isLoading: isLoadingLoginOptions } =
    useLoginOptions();
  const [showMlsSearch, setShowMlsSearch] = useState(
    loginDomainConfig.startsOnMembershipAffiliation,
  );

  const [loginOptionId, setLoginOptionId] = useState<string | null>(() =>
    supportsMembershipAffiliation
      ? localStorage.getItem(LOGIN_OPTION_STORAGE_KEY)
      : null,
  );

  useEffect(() => {
    if (loginOptionId) {
      localStorage.setItem(LOGIN_OPTION_STORAGE_KEY, loginOptionId);
    } else {
      localStorage.removeItem(LOGIN_OPTION_STORAGE_KEY);
    }
  }, [loginOptionId]);

  useEffect(() => {
    if (isLoadingLoginOptions) return;
    if (
      loginOptionId &&
      !getLoginOptionById(availableLoginOptions, loginOptionId)
    ) {
      setLoginOptionId(null);
    }
  }, [isLoadingLoginOptions, loginOptionId, availableLoginOptions]);

  const { isAuthenticated, user, isValidatingCredentials, error } = useAuth();

  useEffect(() => {
    if (error) {
      toast.error(error.message, {
        toastId: "login-auth-error",
        autoClose: 15000,
      });
    }
  }, [error]);
  const {
    loginFormMethods,
    loginMutation,
    oauthInfoQuery,
    loginOAuth2AuthorizeMutation,
    continueAsAuthenticatedUser,
  } = useLoginLogic({
    oauthData,
    redirect,
    welcome,
  });

  const [dismissedSignedInPrompt, setDismissedSignedInPrompt] = useState(false);

  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
  } = loginFormMethods;

  useHandleSSOErrors();

  const forgotPasswordPath = location.search
    ? `/forgot-password${location.search}`
    : "/forgot-password";

  const handleForgotPasswordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const email = getValues("email");
    if (!email?.trim()) {
      navigate(forgotPasswordPath);
      return;
    }
    navigate(`/forgot-password?email=${email}`);
  };

  const handleLogin = useCallback(
    (data: LoginFormSchema) => {
      if (!oauthData) {
        loginMutation.mutate(data);
        return;
      }

      loginOAuth2AuthorizeMutation.mutate({
        ...data,
        ...oauthData,
      });
    },
    [oauthData, loginMutation, loginOAuth2AuthorizeMutation],
  );

  const isSuccess =
    loginMutation.isSuccess || loginOAuth2AuthorizeMutation.isSuccess;
  const isLoading =
    loginMutation.isPending || loginOAuth2AuthorizeMutation.isPending;

  if (isValidatingCredentials) {
    return (
      <AuthContainer>
        <header>
          <img src={logoSrc} alt={logoAlt} />
        </header>
        <LoadingContainer>
          <CircularProgress />
          <Typography variant="body2">Loading...</Typography>
        </LoadingContainer>
      </AuthContainer>
    );
  }

  if (isSuccess) {
    return (
      <AuthContainer>
        <header>
          <img src={logoSrc} alt={logoAlt} />
        </header>
        <LoadingContainer>
          <CircularProgress />
          <Typography variant="body2">Redirecting...</Typography>
        </LoadingContainer>
      </AuthContainer>
    );
  }

  if (oauthData && isAuthenticated && user) {
    return (
      <OAuthAuthorization
        userEmail={user.email}
        isLoadingOAuthClient={oauthInfoQuery.isLoading}
        oauthClientInfo={oauthInfoQuery.data}
      />
    );
  }

  const handleChangeLoginOption = (value: string) => {
    setLoginOptionId(value);
    const option = getLoginOptionById(availableLoginOptions, value);
    if (option) {
      const basePath = getLoginPathByOption(option);
      const pathWithParams = location.search
        ? `${basePath}${location.search}`
        : basePath;
      navigate(pathWithParams);
    }
    setIsEditMlsOptionEnabled(false);
  };

  const handleSelectedMembershipAffiliationClick = () => {
    setIsEditMlsOptionEnabled(true);

    setTimeout(() => {
      if (membershipAffiliationRef.current) {
        membershipAffiliationRef.current.focus();
        setMembershipAffiliationOpen(true);
      }
    }, 100);
  };

  const handleClearLoginOption = () => {
    setLoginOptionId(null);
    setIsEditMlsOptionEnabled(false);
    setShowMlsSearch(loginDomainConfig.startsOnMembershipAffiliation);
  };

  const handleSwitchAccount = () => {
    handleClearLoginOption();
    setDismissedSignedInPrompt(true);
  };

  const selectedLoginOption = getLoginOptionById(
    availableLoginOptions,
    loginOptionId,
  );

  const isResolvingLoginOption = !!loginOptionId && isLoadingLoginOptions;

  const isDefaultView =
    !isResolvingLoginOption && !selectedLoginOption && !showMlsSearch;

  const isEmailFormVisible =
    isDefaultView ||
    (!!selectedLoginOption && !isMlsSsoLoginOption(selectedLoginOption));
  const isSsoSectionVisible =
    isDefaultView ||
    (!!selectedLoginOption && isMlsSsoLoginOption(selectedLoginOption));
  const ssoProviderId = selectedLoginOption?.id ?? INDIVIDUAL_PLAN_ID;
  const isGoogleProvider = ssoProviderId === INDIVIDUAL_PLAN_ID;

  const isAlreadySignedIn =
    isAuthenticated && !oauthData && !dismissedSignedInPrompt;

  const currentUserPlanName =
    user.subscriptionPlanId === INDIVIDUAL_PLAN_ID
      ? INDIVIDUAL_PLAN.name
      : getLoginOptionById(availableLoginOptions, user.subscriptionPlanId)
          ?.name;

  return (
    <AuthContainer>
      <ResponsiveSheetDialog
        open={isAlreadySignedIn}
        onClose={handleSwitchAccount}
        title="You're already signed in"
        maxWidth="sm"
      >
        <SelectedLoginOption
          onClick={continueAsAuthenticatedUser}
          style={{ justifyContent: "space-between" }}
        >
          <Box display="flex" alignItems="center">
            <UserAvatar userName={user.name} userAvatar={user.avatar} />
            <Box ml={1.5} textAlign="left">
              <Typography fontWeight={600}>{user.email}</Typography>
              <Typography variant="caption" component="p">
                {user.name}
                {!!currentUserPlanName && (
                  <>
                    <BulletSeparator />
                    {currentUserPlanName}
                  </>
                )}
              </Typography>
            </Box>
          </Box>
          <ChevronRightIcon />
        </SelectedLoginOption>
        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button variant="contained" onClick={handleSwitchAccount}>
            Switch account
          </Button>
        </Box>
      </ResponsiveSheetDialog>

      <header>
        <img src={logoSrc} alt={logoAlt} />
      </header>

      {!isEditMlsOptionEnabled && (
        <Box textAlign={"center"}>
          <Typography>{loginDomainConfig.heading}</Typography>
          <OAuthClientInfoBox
            isLoading={oauthInfoQuery.isLoading}
            oauthClientInfo={oauthInfoQuery.data}
          />
        </Box>
      )}

      {supportsMembershipAffiliation &&
        (showMlsSearch || !!selectedLoginOption || isResolvingLoginOption) && (
          <MembershipAffiliationContainer aria-disabled={isLoadingLoginOptions}>
            {!isEditMlsOptionEnabled &&
            (!!selectedLoginOption || isResolvingLoginOption) ? (
              <>
                <SelectedLoginOption
                  onClick={handleSelectedMembershipAffiliationClick}
                  data-testid="selected-login-option"
                >
                  {isLoadingLoginOptions ? (
                    <Skeleton width={"75%"} />
                  ) : (
                    <Typography>{selectedLoginOption?.name}</Typography>
                  )}
                </SelectedLoginOption>
                <Box textAlign="center">
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<ArrowBackIcon fontSize="small" />}
                    onClick={handleClearLoginOption}
                  >
                    Back
                  </Button>
                </Box>
              </>
            ) : (
              <Box mt={3}>
                <Typography variant="subtitle1" mb={1} textAlign="left">
                  {loginDomainConfig.membershipAffiliationLabel}
                </Typography>
                <FormControl fullWidth>
                  <Autocomplete
                    open={membershipAffiliationOpen}
                    onClose={() => setMembershipAffiliationOpen(false)}
                    onOpen={() => setMembershipAffiliationOpen(true)}
                    options={availableLoginOptions}
                    getOptionLabel={(option) => option.name}
                    filterOptions={filterOptions}
                    onChange={(_, value) => handleChangeLoginOption(value.id)}
                    disableClearable
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select your Membership Affiliation"
                        inputRef={membershipAffiliationRef}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            )}
          </MembershipAffiliationContainer>
        )}

      {isSsoSectionVisible && (
        <>
          {isGoogleProvider && (
            <GoogleSsoButton
              flow="login"
              variant="outlined"
              buttonText="Continue with Google"
              redirect={redirect}
            />
          )}
          {!isGoogleProvider && !isDev && (
            <LoginSSO provider={ssoProviderId} redirect={redirect} />
          )}
          {!isGoogleProvider && isDev && (
            <Suspense>
              <LoginSSOMock provider={ssoProviderId} redirect={redirect} />
            </Suspense>
          )}
          {isDefaultView && (
            <Divider sx={{ mt: 1, mb: 1 }}>
              <Typography variant="body2">or</Typography>
            </Divider>
          )}
        </>
      )}

      {isEmailFormVisible && (
        <Form onSubmit={handleSubmit(handleLogin)} aria-disabled={isLoading}>
          <TextField
            name={"email"}
            label={"Email address"}
            autoComplete="email"
            inputProps={{ ...register("email") }}
            fieldError={errors.email}
            autoFocus
          />
          <PasswordTextField
            name={"password"}
            label={"Password"}
            autoComplete="current-password"
            inputProps={{ ...register("password") }}
            fieldError={errors.password}
          />

          <SubmitButton type="submit" isLoading={isLoading}>
            Sign in
          </SubmitButton>
          <Typography textAlign="center" mt={1}>
            <Link to={forgotPasswordPath} onClick={handleForgotPasswordClick}>
              Forgot password?
            </Link>
          </Typography>
        </Form>
      )}

      <Box mt={1} textAlign={"center"}>
        <Typography>
          Don&apos;t have an account? <Link to={"/subscribe"}>Register</Link>
        </Typography>
        <BrandSwitchPrompt actionLabel="Sign in here" path="/login" />
      </Box>
    </AuthContainer>
  );
};
